import { create } from 'zustand';
import { useEffect, useCallback } from 'react';

export type Portal = 'public' | 'affiliate' | 'tenant' | 'admin';

interface RouterState {
  currentPath: string;
  params: Record<string, string>;
  navigate: (path: string, params?: Record<string, string>) => void;
  back: () => void;
  matchRoute: (pattern: string) => boolean;
}

function getHashPath(): string {
  if (typeof window === 'undefined') return '/';
  const hash = window.location.hash.slice(1) || '/';
  return hash;
}

export const useRouter = create<RouterState>((set, get) => ({
  currentPath: '/',
  params: {},
  navigate: (path: string, params: Record<string, string> = {}) => {
    set({ currentPath: path, params });
    window.location.hash = path;
    window.scrollTo(0, 0);
  },
  back: () => { window.history.back(); },
  matchRoute: (pattern: string) => {
    const { currentPath } = get();
    if (pattern === currentPath) return true;
    const patternParts = pattern.split('/');
    const pathParts = currentPath.split('/');
    if (patternParts.length !== pathParts.length) return false;
    return patternParts.every((part, i) => {
      if (part.startsWith('[') && part.endsWith(']')) return true;
      return part === pathParts[i];
    });
  },
}));

export function useHashRouter() {
  const navigate = useRouter((s) => s.navigate);
  const handleHashChange = useCallback(() => {
    const path = getHashPath();
    const params: Record<string, string> = {};
    const segments = path.split('/').filter(Boolean);
    const patterns: [string, string[]][] = [
      ['/checkout/[id]', ['id']],
      ['/tenant/agents/[id]', ['id']],
      ['/admin/tenants/[id]', ['id']],
      ['/admin/tenants/new', []],
      ['/admin/users/[id]', ['id']],
      ['/admin/users/new', []],
      ['/tenant/knowledge/[id]', ['id']],
      ['/tenant/knowledge/new', []],
      ['/tenant/business/products/[id]', ['id']],
      ['/tenant/business/products/new', []],
    ];
    for (const [pattern, paramNames] of patterns) {
      const patternSegments = pattern.split('/').filter(Boolean);
      if (segments.length === patternSegments.length) {
        let match = true;
        const extractedParams: Record<string, string> = {};
        for (let i = 0; i < segments.length; i++) {
          if (patternSegments[i].startsWith('[') && patternSegments[i].endsWith(']')) {
            extractedParams[paramNames[i]] = segments[i];
          } else if (patternSegments[i] !== segments[i]) {
            match = false; break;
          }
        }
        if (match) { Object.assign(params, extractedParams); break; }
      }
    }
    navigate(path, params);
  }, [navigate]);
  useEffect(() => {
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [handleHashChange]);
}

export function getPortal(path: string): Portal {
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/tenant')) return 'tenant';
  if (path.startsWith('/affiliate')) return 'affiliate';
  return 'public';
}
